import classNames from 'classnames';
import React, { useRef } from 'react';
import IconButton from '../IconButton';
import isSize from '../../utils/responsive/isSize';
import Sizes from '../../utils/responsive/Sizes';
import closeIcon from '../../icons/non-prefilled/16x16/clear.svg';

interface PopOverContainerProps {
  close?: () => void;
  overflow?: 'visible' | 'auto';
  mobileTitle?: string;
  title?: string;
  width?: 'regular' | 'auto' | 'wide';
  fullHeight?: boolean;
  noMargin?: boolean;
  noPadding?: boolean;
  children:
    | React.ReactNode
    | ((args: { scrollToBottom: () => void }) => React.ReactNode);
}

const PopOverContainer = ({
  children,
  close,
  width = 'regular',
  overflow = 'auto',
  title,
  mobileTitle = title,
  fullHeight,
  noMargin,
  noPadding,
}: PopOverContainerProps) => {
  const scrollElementRef = useRef<HTMLDivElement>(null);

  const finalTitle = isSize(Sizes.SM) ? mobileTitle : title;

  const scrollToBottom = () => {
    if (scrollElementRef.current) {
      scrollElementRef.current.scrollTop =
        scrollElementRef.current.scrollHeight;
    }
  };

  const inner =
    typeof children === 'function'
      ? children({
          scrollToBottom,
        })
      : children;

  return (
    <div
      className={classNames('pop-over-container', {
        'width-auto': width === 'auto',
        'width-wide': width === 'wide',
        'show-margin': !noMargin,
        'max-height-full': fullHeight,
      })}
    >
      <div className="pop-over-content-wrapper">
        {finalTitle && (
          <div className="pop-over-container-header">
            <div className="pop-over-container-title">{finalTitle}</div>
            <IconButton
              className="pop-over-container-close"
              onClick={close}
              applyNegativeMargin
              aria-label="Close"
            >
              {closeIcon}
            </IconButton>
          </div>
        )}
        <div
          className={classNames('pop-over-container-content', {
            'overflow-visible': overflow === 'visible',
          })}
          ref={scrollElementRef}
        >
          {noPadding ? (
            inner
          ) : (
            <div className="pop-over-container-content-inner">{inner}</div>
          )}
        </div>
      </div>
    </div>
  );
};

interface PopOverContainerArrowProps
  extends React.HTMLAttributes<HTMLDivElement> {
  noMargin?: boolean;
  small?: boolean;
}

const PopOverContainerArrow = React.forwardRef<
  HTMLDivElement,
  PopOverContainerArrowProps
>(({ noMargin, small, ...props }, ref) => (
  <div
    ref={ref}
    className={classNames('pop-over-container-arrow', {
      'show-margin': !noMargin,
      'pop-over-container-arrow-small': small,
    })}
    {...props}
  />
));

PopOverContainer.Arrow = PopOverContainerArrow;

export default PopOverContainer;
